import { MfsCollectionKind, } from '@metaos/mfs-client';
import { MfsError, MfsStatusCode } from '@metaos/mfs-telemetry';
import { ArgumentException, AssertionError, ValidationError } from '../errors';
import { mfsErrorHandler } from '../errors/mfsErrorHandler';
import { toCard } from '../utility/cardUtils';
import { MFS_CARD_TYPE, TAG_CONTAINER_ID } from '../utility/constant/constants';
import { containsRelationship } from '../utility/constant/internalRelationships';
import { logger } from '../utility/telemetry';
import { getMfsItem, getMfsItemOrThrowError } from '../utility/utils';
import { getMfsCardTypeguard } from '../validation/typeGuard/folderInternalTypeGuard';
import { isCardInput } from '../validation/typeGuard/folderTypeGuard';
import { throwsIfContainsReservedProperties, throwsIfEmptyOrWhitespace } from '../validation/validation';
/** @inheritdoc */
export class CardClient {
    constructor(dataStore, cardPropertyUtils) {
        this.dataStore = dataStore;
        this.cardPropertyUtils = cardPropertyUtils;
    }
    /** @inheritdoc */
    async patchCard(cardId, propertyUpdates) {
        throwsIfContainsReservedProperties(propertyUpdates);
        const { uniqueId, ...sanitizedPatch } = propertyUpdates;
        if (uniqueId && uniqueId !== cardId) {
            throw new ArgumentException('The unique id provided on the patch property does not match the original');
        }
        const mfsCard = getMfsItemOrThrowError(this.dataStore, cardId, MFS_CARD_TYPE);
        try {
            await this.dataStore.patchItem(mfsCard.id, sanitizedPatch);
            logger.logVerbose('patchCard', `Patched Card`, {
                itemId: mfsCard.id,
            });
            return this.cardPropertyUtils.refreshUpdatedAtTimeSafe(mfsCard);
        }
        catch (error) {
            return Promise.reject(mfsErrorHandler(`Failed to patch Card due to unexpected error.`, error));
        }
    }
    /** @inheritdoc */
    async addCard(cardId, parentCardId) {
        throwsIfEmptyOrWhitespace(cardId, 'cardId');
        throwsIfEmptyOrWhitespace(parentCardId, 'parentCardId');
        const parentMfsCard = getMfsItemOrThrowError(this.dataStore, parentCardId, MFS_CARD_TYPE);
        const childMfsCard = getMfsItemOrThrowError(this.dataStore, cardId, MFS_CARD_TYPE);
        if (this.dataStore.hasElement(parentMfsCard.id, childMfsCard.id, 1)) {
            throw new AssertionError(`Card ${childMfsCard.id} is already a child of Parent ${parentMfsCard.id}.`);
        }
        try {
            const lastPosition = { place: 'last' };
            this.dataStore.insertItem(parentMfsCard.id, childMfsCard.id, lastPosition);
            this.dataStore.addRelatedItem(parentMfsCard.id, containsRelationship, childMfsCard.id);
            logger.logVerbose('addCard', 'Added Card', {
                parentItemId: parentMfsCard.id,
                childItemId: childMfsCard.id,
            });
            return this.cardPropertyUtils.refreshUpdatedAtTimeSafe(childMfsCard);
        }
        catch (error) {
            return Promise.reject(mfsErrorHandler(`Failed to add a Card due to unexpected error`, error));
        }
    }
    /** @inheritdoc */
    removeFromParent(cardId, parentCardId) {
        throwsIfEmptyOrWhitespace(cardId, 'cardId');
        throwsIfEmptyOrWhitespace(parentCardId, 'parentCardId');
        const parentMfsCard = getMfsItemOrThrowError(this.dataStore, parentCardId, MFS_CARD_TYPE);
        const childMfsCard = getMfsItemOrThrowError(this.dataStore, cardId, MFS_CARD_TYPE);
        try {
            for (const entry of this.dataStore.getElementIdsOf(parentMfsCard.id, childMfsCard.id, 1)) {
                this.dataStore.removeItemAt(parentMfsCard.id, { place: 'at', elementId: entry.elementId });
            }
            this.dataStore.removeRelatedItem(parentMfsCard.id, containsRelationship, childMfsCard.id);
            logger.logVerbose('removeFromParent', 'Removed Card', {
                parentItemId: parentMfsCard.id,
                childItemId: childMfsCard.id,
            });
            return this.cardPropertyUtils.refreshUpdatedAtTimeSafe(childMfsCard);
        }
        catch (error) {
            throw mfsErrorHandler(`Failed to remove Card due to unexpected error.`, error);
        }
    }
    /** @inheritdoc */
    async createCard(card) {
        const createCardInternal = async (item) => {
            const mfsCard = await this.dataStore.createCollection(item);
            logger.logVerbose('createCard', `Created Card`, { itemId: mfsCard.id });
            return this.cardPropertyUtils.refreshUpdatedAtTimeSafe(mfsCard);
        };
        this.validateCardInput(card);
        if (card.uniqueId === TAG_CONTAINER_ID) {
            return Promise.reject(new ValidationError(`Unique Id cannot be equal to a reserved string ${card.uniqueId}.`));
        }
        const mfsItem = this.toMfsCard(card);
        // Try to create the card. If a NotAllowed is thrown from MFS, but the card can't be retrieved, try to delete the card to clear up locked labelIds, and then try to create the card again.
        try {
            return await createCardInternal(mfsItem);
        }
        catch (error) {
            if (error instanceof MfsError &&
                (error.statusCode === MfsStatusCode.NotAllowed || error.statusCode === MfsStatusCode.Consensus)) {
                try {
                    // Check if the full card already exists.
                    if (getMfsItem(this.dataStore, card.uniqueId, MFS_CARD_TYPE)) {
                        return Promise.reject(new ValidationError('Failed to create the Card when target Card already exists.'));
                    }
                    logger.logVerbose('createCard', `Failed to create the card. Attemping to delete the existing locked card data before creating the card again.`);
                    await this.deleteCard(card.uniqueId); // No duplicate card, but NotAllowed potentially means the id is locked.
                    logger.logVerbose('createCard', `Finished attempting to delete the existing locked card data. Attempting to create the card again.`);
                    return await createCardInternal(mfsItem); // Finally try to create the card again.
                }
                catch (errorInternal) {
                    return Promise.reject(mfsErrorHandler(`Failed to create the Card due to unexpected error`, error)); // throw the original error
                }
            }
            return Promise.reject(mfsErrorHandler(`Failed to create the Card due to unexpected error`, error));
        }
    }
    /** @inheritdoc */
    getCard(cardId) {
        throwsIfEmptyOrWhitespace(cardId, 'cardId');
        try {
            const mfsCard = getMfsItem(this.dataStore, cardId, MFS_CARD_TYPE);
            if (!mfsCard) {
                logger.logVerbose('getCard', `Card not found. Returning.`);
                return undefined;
            }
            const item = toCard(this.dataStore, mfsCard);
            logger.logVerbose('getCard', 'Retrieved Card', { itemId: mfsCard.id });
            return item;
        }
        catch (error) {
            throw mfsErrorHandler('Failed to get Card due to unexpected error', error);
        }
    }
    /** @inheritdoc */
    *getCards(query) {
        try {
            let mfsQuery = undefined;
            if ((query === null || query === void 0 ? void 0 : query.top) || (query === null || query === void 0 ? void 0 : query.skip) || (query === null || query === void 0 ? void 0 : query.filter)) {
                mfsQuery = {
                    top: query.top,
                    skip: query.skip,
                    filter: query.filter
                        ? (item) => {
                            const card = toCard(this.dataStore, item, true);
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            return query.filter(card);
                        }
                        : undefined,
                };
            }
            for (const item of this.dataStore.getItems(getMfsCardTypeguard(), mfsQuery)) {
                const card = toCard(this.dataStore, item, true);
                yield card;
            }
        }
        catch (err) {
            throw mfsErrorHandler(`Failed to get Cards due to unexpected error: ${err}`, err, 'GetCards');
        }
    }
    /** @inneritdoc */
    async deleteCard(cardId) {
        throwsIfEmptyOrWhitespace(cardId, 'cardId');
        const mfsCard = getMfsItem(this.dataStore, cardId, MFS_CARD_TYPE);
        if ((mfsCard === null || mfsCard === void 0 ? void 0 : mfsCard.labelId) === undefined) {
            try {
                logger.logVerbose('deleteCard', 'Card was not found. Attempting to clean up a potentially locked labelId.');
                await this.dataStore.deleteItemByLabelId(MFS_CARD_TYPE, cardId);
                logger.logVerbose('deleteCard', 'deleteItemByLabelId has been completed.');
                return;
            }
            catch (error) {
                throw mfsErrorHandler(`Failed to delete Card due to unexpected error: ${error}`, error, 'DeleteCard');
            }
        }
        try {
            logger.logVerbose('deleteCard', 'Retrieved Card', { itemId: mfsCard.id });
            const relatedItems = Array.from(this.dataStore.getRelatedItems(mfsCard.id, containsRelationship));
            if (relatedItems.length > 0) {
                const relatedCardIds = relatedItems.flatMap((item) => item.id);
                logger.logVerbose('deleteCard', 'Card contains children. Attempting to remove all', {
                    itemId: mfsCard.id,
                    childItemIds: relatedCardIds,
                });
                this.dataStore.removeRelatedItems(mfsCard.id, containsRelationship, relatedCardIds);
                logger.logVerbose('deleteCard', 'Child cards have been unparented from target Card', {
                    itemId: mfsCard.id,
                    childItemIds: relatedCardIds,
                });
            }
            else {
                logger.logVerbose('deleteCard', 'Card does not contain any children', { itemId: mfsCard.id });
            }
            await this.dataStore.deleteItemByLabelId(mfsCard.label, mfsCard.labelId);
            logger.logVerbose('deleteCard', `Card has been deleted`, { itemId: mfsCard.id });
        }
        catch (error) {
            throw mfsErrorHandler(`Failed to delete Card due to unexpected error: ${error}`, error, 'DeleteCard');
        }
    }
    /** Validate if input value is a CardInput and throws error if input is invalid or contains reserved properties. */
    validateCardInput(input) {
        if (!input) {
            throw new ArgumentException('Provided CardInput value cannot be null or undefined');
        }
        if (!isCardInput(input)) {
            throw new ArgumentException('Provided value is not defined as valid CardInput.');
        }
        throwsIfContainsReservedProperties(input);
    }
    /** Helper function converting a Card to the app-specific collection type of card.*/
    toMfsCard(card) {
        const { uniqueId, ...sanatizedItem } = card;
        return {
            ...sanatizedItem,
            label: MFS_CARD_TYPE,
            labelId: uniqueId,
            displayText: card.name,
            kind: MfsCollectionKind.OrderedList,
        };
    }
}
//# sourceMappingURL=cardClient.js.map